import { useEffect, useRef } from 'react';
import { withSearch } from '@elastic/react-search-ui';
import { useHistoryOverride } from '../../context/HistoryOverrideContext';
import { useLocation } from 'react-router-dom';

interface ApplyExactFilterProps {
  setFilter: (field: string, value: string, type: 'all' | 'any' | 'none') => void;
}

const wrapInRaw = (flat: Record<string, any>): Record<string, any> => {
  const wrapped: Record<string, any> = {};
  for (const key of Object.keys(flat)) {
    const val = flat[key];
    wrapped[key] =
      val !== null && typeof val === 'object' && 'raw' in val
        ? val
        : { raw: val };
  }
  return wrapped;
};

const ApplyExactFilter: React.FC<ApplyExactFilterProps> = ({ setFilter }) => {
  const {
    setOverrideResults,
    setOverrideHash,
    setOverrideTitle,
    setIsLoading,
  } = useHistoryOverride();
  const location = useLocation();
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;

    // Check for history navigation state (from VehicleDetail "View full car history" click)
    const historyHash = location.state?.historyHash;
    const historyTitle = location.state?.historyTitle;

    // Also check URL param for direct links
    const params = new URLSearchParams(window.location.search);
    const urlHash = params.get('config_hash_exact');

    const configHash = historyHash || urlHash;
    if (!configHash) return;

    // Clean URL if came from URL param
    if (urlHash) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    setIsLoading(true);
    setOverrideTitle(historyTitle || null);
    setOverrideHash(configHash);

    fetch(
      `https://mynextclassic-backend.net/listings-by-hash?config_hash_exact=${configHash}&brand=porsche`
    )
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const raw: any[] = data.results ?? [];

        const seen = new Map<string, any>();
        for (const item of raw) {
          const uuid = item.car_uuid;
          if (!uuid) continue;
          const existing = seen.get(uuid);
          if (!existing) {
            seen.set(uuid, item);
          } else {
            if ((item.scraped_at ?? '') > (existing.scraped_at ?? '')) {
              seen.set(uuid, item);
            }
          }
        }

        const deduplicated = Array.from(seen.values()).map(wrapInRaw);
        setOverrideResults(deduplicated);
        setIsLoading(false);
        console.log(`✅ ${deduplicated.length} deduplicated results in drawer`);
      })
      .catch((err) => {
        console.error('Failed to fetch history:', err);
        setOverrideResults([]);
        setIsLoading(false);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return null;
};

export default withSearch(({ setFilter }) => ({ setFilter }))(ApplyExactFilter);